// CoD (Cash on Delivery) domain types for Djayma e-commerce

import type { RegionId } from "@/lib/constants";

/** Status lifecycle for a Cash on Delivery order */
export enum CodOrderStatus {
  PENDING_VERIFICATION = "PENDING_VERIFICATION",
  VERIFIED = "VERIFIED",
  OUT_FOR_DELIVERY = "OUT_FOR_DELIVERY",
  DELIVERED_PAID = "DELIVERED_PAID",
  DELIVERED_UNPAID = "DELIVERED_UNPAID",
  CANCELLED = "CANCELLED",
  RETURNED = "RETURNED",
}

/** Admin-configurable CoD settings */
export interface CodConfig {
  readonly enabled: boolean;
  /** Minimum order amount in FCFA */
  readonly minAmount: number;
  /** Maximum order amount in FCFA */
  readonly maxAmount: number;
  /** Region IDs where CoD is allowed */
  readonly allowedZones: readonly string[];
  /** Whether phone verification is required before order confirmation */
  readonly requirePhoneVerification: boolean;
  /** Max concurrent pending CoD orders per customer */
  readonly maxPendingOrders: number;
}

/** A Cash on Delivery order record */
export interface CodOrder {
  readonly orderId: string;
  readonly customerPhone: string;
  /** Order total in FCFA (integer, no decimals) */
  readonly amount: number;
  readonly deliveryAddress: string;
  readonly status: CodOrderStatus;
  /** 4-digit SMS verification code */
  readonly verificationCode: string;
  readonly deliveryAgentId?: string;
  readonly confirmedAt?: string;
  /** Amount actually collected by the delivery agent in FCFA */
  readonly collectedAmount?: number;
}

/** SMS verification record for a CoD order */
export interface CodVerification {
  readonly orderId: string;
  /** 4-digit numeric code */
  readonly code: string;
  /** Customer phone in +221 format */
  readonly phone: string;
  /** ISO date string */
  readonly expiresAt: string;
  readonly verified: boolean;
}

/** Result of an eligibility check */
export interface CodEligibilityResult {
  readonly eligible: boolean;
  readonly reason?: string;
  /** The threshold amount relevant to the reason (e.g. maxAmount for max_amount, minAmount for min_amount) */
  readonly threshold?: number;
}
